import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Rocket,
  Play,
  RotateCcw,
  Trophy,
  Frown,
  Circle,
  Globe,
  CloudMoon,
} from "lucide-react";
import GameHeader from "./GameHeader";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { createCardQueue } from "@/lib/cardQueue";

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function generateOptions(currentCard: Card, allCards: Card[]): string[] {
  const correct = (currentCard.back || "").trim();
  const others = allCards.filter(
    (c) => c !== currentCard && (c.back || "").trim()
  );
  const uniqueBacks = [...new Set(others.map((c) => (c.back || "").trim()).filter(Boolean))].filter(
    (b) => b !== correct
  );
  const shuffled = shuffleArray(uniqueBacks);
  const distractors: string[] = [];
  for (let i = 0; distractors.length < 3 && i < shuffled.length * 2; i++) {
    const pick = shuffled[i % Math.max(1, shuffled.length)];
    if (pick && !distractors.includes(pick)) distractors.push(pick);
  }
  while (distractors.length < 3 && uniqueBacks[0]) {
    distractors.push(uniqueBacks[0]);
  }
  return shuffleArray([correct, ...distractors.slice(0, 3)]);
}

type GameState = "intro" | "playing" | "won" | "lost";

const WIN_SCORE_THRESHOLD = 2000;
const BASE_FALL_INTERVAL_MS = 50;
const WORDS_PER_LEVEL = 5;
const BASE_POINTS = 100;
const FALL_DURATION_L1_MS = 10000;
const FALL_DURATION_MIN_MS = 3000;
const FALL_DURATION_DECAY = 0.9;

interface DefenderModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
  onFavorite?: () => void;
  isFavorite?: boolean;
  onCardResult?: (cardId: string, correct: boolean) => void;
}

export default function DefenderMode({ cards, lang: deckLang, onBack, onProgressComplete, onFavorite, isFavorite = false, onCardResult }: DefenderModeProps) {
  const { t } = useTranslation();
  const { isSoundEnabled, isVoiceEnabled, gameVolume, voiceVolume, toggleSound: storeToggleSound } = useSoundStore();
  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );
  const totalWords = filteredCards.length;

  const [gameState, setGameState] = useState<GameState>("intro");
  const [health, setHealth] = useState(3);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(1);
  const [level, setLevel] = useState(1);
  const [wordsCleared, setWordsCleared] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [currentWord, setCurrentWord] = useState<Card | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [asteroidProgress, setAsteroidProgress] = useState(0);
  const [wrongShake, setWrongShake] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);

  const fallIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const collisionProcessedRef = useRef(false);
  const cardQueueRef = useRef(createCardQueue());

  const stars = useMemo(() => {
    return Array.from({ length: 50 }, () => ({
      top: Math.random() * 100,
      left: Math.random() * 100,
      sizePx: 1 + Math.random() * 2,
      delay: Math.random() * 2,
    }));
  }, []);
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const winMusicRef = useRef<HTMLAudioElement | null>(null);
  const loseMusicRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
      if (fallIntervalRef.current) {
        clearInterval(fallIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const vol = isSoundEnabled ? gameVolume : 0;
    if (bgMusicRef.current) { bgMusicRef.current.volume = vol; if (!isSoundEnabled) bgMusicRef.current.pause(); }
    if (winMusicRef.current) { winMusicRef.current.volume = vol; if (!isSoundEnabled) winMusicRef.current.pause(); }
    if (loseMusicRef.current) { loseMusicRef.current.volume = vol; if (!isSoundEnabled) loseMusicRef.current.pause(); }
  }, [gameVolume, isSoundEnabled]);

  useEffect(() => {
    if (gameState === "won" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      winMusicRef.current?.pause();
      if (winMusicRef.current) {
        winMusicRef.current.currentTime = 0;
        winMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState === "lost" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      loseMusicRef.current?.pause();
      if (loseMusicRef.current) {
        loseMusicRef.current.currentTime = 0;
        loseMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState !== "playing") {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
    }
  }, [gameState, isSoundEnabled]);

  const toggleSound = useCallback(() => {
    storeToggleSound();
    if (!useSoundStore.getState().isSoundEnabled) {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
    } else if (gameState === "playing" && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.volume = gameVolume;
      bgMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
    }
  }, [gameState, storeToggleSound, gameVolume]);

  const getPoolIndices = useCallback(() => {
    return filteredCards.map((_, i) => i);
  }, [filteredCards]);

  const loadNextWord = useCallback(() => {
    collisionProcessedRef.current = false;
    const idx = cardQueueRef.current.getNext(filteredCards, getPoolIndices);
    if (idx !== null) {
      const card = filteredCards[idx];
      setCurrentWord(card);
      setOptions(generateOptions(card, filteredCards));
      const term = (card.front || "").trim();
      if (useSoundStore.getState().isVoiceEnabled && term) {
        void playAudio({ card, side: "front", deckLang: deckLang ?? "en", volume: voiceVolume });
      }
    } else {
      setCurrentWord(null);
      setOptions([]);
    }
    setAsteroidProgress(0);
  }, [filteredCards, getPoolIndices, voiceVolume, deckLang]);

  const startGame = useCallback(() => {
    cardQueueRef.current.reset();
    setGameState("playing");
    setHealth(3);
    setScore(0);
    setCombo(1);
    setLevel(1);
    setWordsCleared(0);
    setShowLevelUp(false);
    loadNextWord();
    if (isSoundEnabled && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.play().catch(console.error);
    }
  }, [loadNextWord, isSoundEnabled]);

  const resetGame = useCallback(() => {
    if (fallIntervalRef.current) {
      clearInterval(fallIntervalRef.current);
      fallIntervalRef.current = null;
    }
    setGameState("intro");
    setHealth(3);
    setScore(0);
    setCombo(1);
    setLevel(1);
    setWordsCleared(0);
    setCurrentWord(null);
    setOptions([]);
    setAsteroidProgress(0);
    setShowLevelUp(false);
  }, []);

  const tryAgain = useCallback(() => {
    winMusicRef.current?.pause();
    if (winMusicRef.current) winMusicRef.current.currentTime = 0;
    loseMusicRef.current?.pause();
    if (loseMusicRef.current) loseMusicRef.current.currentTime = 0;
    startGame();
  }, [startGame]);

  const handleCorrect = useCallback(() => {
    if (gameState !== "playing" || !currentWord) return;
    if (currentWord.id && onCardResult) onCardResult(currentWord.id, true);
    const nextWordsCleared = wordsCleared + 1;
    if (totalWords > 0 && onProgressComplete) {
      const pct = Math.min(100, Math.round((nextWordsCleared / totalWords) * 100));
      onProgressComplete(pct);
    }
    setWordsCleared((prev) => {
      const next = prev + 1;
      const newLevel = 1 + Math.floor(next / WORDS_PER_LEVEL);
      setLevel(newLevel);
      if ((prev + 1) % WORDS_PER_LEVEL === 0 && prev + 1 > 0) {
        setShowLevelUp(true);
        setTimeout(() => setShowLevelUp(false), 2000);
      }
      return next;
    });
    setScore((prev) => {
      const newLevel = 1 + Math.floor(nextWordsCleared / WORDS_PER_LEVEL);
      const points = BASE_POINTS * newLevel * combo;
      const next = prev + points;
      if (next >= WIN_SCORE_THRESHOLD || nextWordsCleared >= totalWords) {
        setGameState("won");
        if (fallIntervalRef.current) {
          clearInterval(fallIntervalRef.current);
          fallIntervalRef.current = null;
        }
      }
      return next;
    });
    setCombo((prev) => Math.min(prev + 1, 5));
    loadNextWord();
  }, [gameState, currentWord, combo, wordsCleared, loadNextWord, totalWords, onProgressComplete, onCardResult]);

  const handleWrong = useCallback(() => {
    if (gameState !== "playing" || !currentWord) return;
    if (currentWord.id && onCardResult) onCardResult(currentWord.id, false);
    setCombo(1);
    setWrongShake(true);
    setTimeout(() => setWrongShake(false), 400);
  }, [gameState, currentWord, onCardResult]);

  const handleCollision = useCallback(() => {
    if (gameState !== "playing") return;
    setHealth((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setGameState("lost");
        if (fallIntervalRef.current) {
          clearInterval(fallIntervalRef.current);
          fallIntervalRef.current = null;
        }
      }
      return next;
    });
    setCombo(1);
    loadNextWord();
  }, [gameState, loadNextWord]);

  const handleSelectOption = useCallback(
    (option: string) => {
      if (!currentWord || gameState !== "playing") return;
      const isCorrect = option === (currentWord.back || "").trim();
      if (isCorrect) {
        handleCorrect();
      } else {
        handleWrong();
      }
    },
    [currentWord, gameState, handleCorrect, handleWrong]
  );

  useEffect(() => {
    if (gameState !== "playing" || !currentWord) return;

    const durationMs = Math.max(
      FALL_DURATION_MIN_MS,
      FALL_DURATION_L1_MS * Math.pow(FALL_DURATION_DECAY, level - 1)
    );
    const progressStep = (100 * BASE_FALL_INTERVAL_MS) / durationMs;

    fallIntervalRef.current = setInterval(() => {
      setAsteroidProgress((prev) => Math.min(100, prev + progressStep));
    }, BASE_FALL_INTERVAL_MS);

    return () => {
      if (fallIntervalRef.current) {
        clearInterval(fallIntervalRef.current);
        fallIntervalRef.current = null;
      }
    };
  }, [gameState, currentWord, level]);

  useEffect(() => {
    if (gameState !== "playing" || asteroidProgress < 100) return;
    if (collisionProcessedRef.current) return;
    collisionProcessedRef.current = true;
    handleCollision();
  }, [gameState, asteroidProgress, handleCollision]);

  useEffect(() => {
    if ((gameState === "won" || gameState === "lost") && score > highScore) {
      setHighScore(score);
    }
  }, [gameState, score, highScore]);

  useEffect(() => {
    if (gameState === "won") {
      const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];
      const fire = (ratio: number, opts: confetti.Options = {}) => {
        confetti({
          ...opts,
          colors,
          origin: opts.origin ?? { y: 0.7 },
          particleCount: Math.floor(200 * ratio),
          shapes: ["circle", "square"],
          scalar: opts.scalar ?? 1.2,
        });
      };
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91 });
    }
  }, [gameState]);

  const topNavRow = (
    <GameHeader
      compact
      onBack={onBack}
      centerContent={
        gameState === "playing" ? (
          <div className="text-sm font-medium text-[hsl(var(--muted-foreground))] tabular-nums">
            {t("defender.wordsCount", { cleared: wordsCleared, total: totalWords, defaultValue: `${wordsCleared} / ${totalWords}` })}
          </div>
        ) : undefined
      }
    />
  );

  if (filteredCards.length < 4) {
    return (
      <>
        <audio ref={bgMusicRef} src="/defender-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/defender-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/defender-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-5xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <p className="text-lg font-medium text-[hsl(var(--foreground))]">
              {t("defender.needCards")}
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
              {t("defender.addMoreCards")}
            </p>
          </div>
        </div>
      </>
    );
  }

  if (gameState === "intro") {
    return (
      <>
        <audio ref={bgMusicRef} src="/defender-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/defender-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/defender-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-5xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center w-full max-w-lg mx-auto py-8 px-6">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full scale-150" />
              <Rocket
                className="relative w-20 h-20 text-blue-400"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-3">
              {t("defender.title")}
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] text-center mb-8 max-w-sm">
              {t("defender.subtitle")}
            </p>
            <button
              type="button"
              onClick={startGame}
              className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--accent2))] hover:opacity-90 transition-all hover:scale-105 active:scale-95 shadow-lg"
            >
              <Play className="w-6 h-6" strokeWidth={2.5} />
              {t("defender.startGame")}
            </button>
          </div>
        </div>
      </>
    );
  }

  const statsEndScreen = (
    <div className="flex flex-col w-full max-w-lg mx-auto py-8 px-6">
      {gameState === "won" ? (
        <Trophy
          className="w-20 h-20 text-yellow-500 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      ) : (
        <Frown
          className="w-24 h-24 text-gray-400/80 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      )}
      <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-8">
        {gameState === "won" ? t("defender.victory") : t("defender.rocketDestroyed")}
      </h1>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.yourScore")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{score}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.wordsCleared")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{wordsCleared}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.yourLevel")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{level}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.highScore")}
          </p>
          <p className="text-2xl font-bold text-amber-500">{Math.max(highScore, score)}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={tryAgain}
        className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--primary))] hover:opacity-90 transition-all w-full max-w-xs mx-auto"
      >
        <RotateCcw className="w-5 h-5" />
        {t("defender.tryAgain")}
      </button>
    </div>
  );

  if (gameState === "won" || gameState === "lost") {
    return (
      <>
        <audio ref={bgMusicRef} src="/defender-bg.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/defender-win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/defender-lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-5xl mx-auto">
          {topNavRow}
          {statsEndScreen}
        </div>
      </>
    );
  }

  return (
    <>
      <audio ref={bgMusicRef} src="/defender-bg.mp3" loop preload="auto" />
      <audio ref={winMusicRef} src="/defender-win.mp3" preload="auto" />
      <audio ref={loseMusicRef} src="/defender-lose.mp3" preload="auto" />
      <div
        className="flex flex-col h-[100dvh] w-full max-w-5xl mx-auto overflow-hidden"
        style={{ paddingBottom: "max(9rem, calc(6rem + env(safe-area-inset-bottom, 0px)))" }}
      >
        {topNavRow}

        <div className="flex flex-col flex-1 min-h-0 w-full relative">
          <div className="relative w-full flex-1 min-h-0 rounded-[32px] bg-slate-900 border border-slate-700/60 shadow-[0_8px_40px_rgba(0,0,0,.45)] overflow-hidden">
            {/* Animated space background - z-0 */}
            <div className="absolute inset-0 z-0 pointer-events-none">
              {/* Starfield layer */}
              <div className="absolute inset-0">
                {stars.map((star, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full bg-white animate-twinkle"
                    style={{
                      top: `${star.top}%`,
                      left: `${star.left}%`,
                      width: `${star.sizePx}px`,
                      height: `${star.sizePx}px`,
                      minWidth: `${star.sizePx}px`,
                      minHeight: `${star.sizePx}px`,
                      animationDelay: `${star.delay}s`,
                    }}
                    aria-hidden
                  />
                ))}
              </div>
              {/* Floating celestial objects */}
              <Circle
                className="absolute w-24 h-24 text-slate-700/30 top-10 left-10 animate-float-slow"
                strokeWidth={1}
                aria-hidden
              />
              <Globe
                className="absolute w-20 h-20 text-slate-600/25 top-[30%] right-8 animate-float-slow"
                style={{ animationDelay: "-5s" }}
                strokeWidth={1}
                aria-hidden
              />
              <CloudMoon
                className="absolute w-28 h-28 text-slate-600/20 bottom-[25%] left-[15%] animate-float-slow"
                style={{ animationDelay: "-10s" }}
                strokeWidth={1}
                aria-hidden
              />
              <Circle
                className="absolute w-16 h-16 text-slate-600/20 top-[60%] right-[20%] animate-float-slow"
                strokeWidth={1}
                style={{ animationDelay: "-15s" }}
                aria-hidden
              />
            </div>

            {/* Game elements - z-10 */}
            <div className="absolute inset-0 flex flex-col z-10">
              <header className="flex justify-between items-center px-5 py-2.5 border-b border-slate-700/40 bg-slate-900/70 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                  <span className="text-lg font-bold text-white">
                    {t("defender.score")}: {score}
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      combo > 1 ? "text-amber-400" : "text-slate-400"
                    }`}
                  >
                    x{combo}
                  </span>
                  <span className="text-sm font-medium text-slate-300">
                    {t("defender.level")}: {level}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3].map((i) => (
                    <Heart
                      key={i}
                      fill={i <= health ? "currentColor" : "none"}
                      className={`w-6 h-6 ${
                        i <= health ? "text-red-500" : "text-slate-600"
                      }`}
                      strokeWidth={2}
                      aria-hidden
                    />
                  ))}
                </div>
              </header>

              <div className="flex-1 relative z-10">
                {currentWord && (
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 transition-all duration-75 ease-linear z-10 ${
                      wrongShake ? "animate-shake" : ""
                    }`}
                    style={{
                      top: `calc(${asteroidProgress}% - 40px)`,
                    }}
                  >
                    <div className="px-6 py-3 rounded-xl bg-gradient-to-br from-amber-800/90 to-amber-900/90 border-2 border-amber-600/80 shadow-lg shadow-amber-900/40">
                      <p className="text-xl font-bold text-amber-100 text-center whitespace-nowrap">
                        {currentWord.front}
                      </p>
                    </div>
                  </div>
                )}

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                  <Rocket
                    className="w-16 h-16 text-blue-400"
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>

                {showLevelUp && (
                  <div
                    className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
                    aria-live="polite"
                  >
                    <p className="text-yellow-400 text-4xl font-bold animate-bounce drop-shadow-lg">
                      {t("defender.levelUp", { level })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3 shrink-0">
            {options.filter(Boolean).map((opt, idx) => (
              <button
                key={`${opt}-${idx}`}
                type="button"
                onClick={() => handleSelectOption(opt)}
                className="min-h-[56px] px-5 py-3 rounded-2xl font-semibold text-[15px] text-left transition-all duration-200 border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:border-[hsl(var(--primary)/.5)] hover:bg-[hsl(var(--bg3))] hover:shadow-md active:scale-[0.97] text-[hsl(var(--foreground))]"
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
